import { Component, validateDictionary } from './live-codec.js';

export const Rule = Object.freeze({ Waiting: 1, Setup: 2, Arena: 4, Mvm: 8, Tournament: 16, Competitive: 32, Koth: 64, Stopwatch: 128, Overtime: 256 });
const thresholds = [60, 30, 10, 5, 4, 3, 2, 1];
const begins = n => `Announcer.RoundBegins${n}Seconds`;
const remaining = (d, time) => Math.max(0, d.flags & 8 && d.flags & 16 ? d.total : d.flags & 1 ? d.remaining : (d.end - time) / 1000);

/** SDK client rules, driven by the renderer's epoch clock; no wall-clock timers. */
export class LiveAnnouncer {
    constructor({ emit = () => {}, prepare = () => {} } = {}) { this.emit = emit; this.prepare = prepare; this.clear(); }
    clear() {
        this.pending = []; this.rows = new Map(); this.timers = new Map(); this.points = new Map(); this.seen = new Set();
        this.rules = null; this.wave = null; this.spies = 0; this.first = true; this.time = 0; this.arena = null;
        this.restartTime = this.waveTime = null; this.flawless = null;
    }
    accept(message) {
        const changes = message.changes.filter(c => c.components[Component.AnnouncerState]);
        this.pending.push({ time: message.sampleTime, changes, deleted: message.deleted ?? [], replace: !!message.replace,
            present: message.replace ? new Set(changes.map(c => c.key)) : null });
        // Bound stalled render queues. A discontinuity seeds the latest state.
        if (this.pending.length > 128) {
            const latest = new Map(this.rows);
            for (const p of this.pending) {
                if (p.replace) for (const key of latest.keys()) if (!p.present.has(key)) latest.delete(key);
                for (const key of p.deleted) latest.delete(key);
                for (const c of p.changes) latest.set(c.key, c);
            }
            const time = this.pending.at(-1).time; this.clear();
            this.pending.push({ time, changes: [...latest.values()], deleted: [], replace: true, present: new Set(latest.keys()) });
        }
    }
    cue(entry, identity, time, seconds = 1) {
        if (entry && this.audible && this.time - time < seconds * 1000) this.emit({ entry, identity, time, validUntil: time + seconds * 1000 });
    }
    timerEligible(d, time) {
        const r = this.rules; if (!r || !this.wave || r.halloween === 4 || r.flags & Rule.Waiting || d.flags & 1 || !(d.flags & 4)) return false;
        if ((r.flags & (Rule.Tournament | Rule.Stopwatch)) === (Rule.Tournament | Rule.Stopwatch)) {
            const active = [...this.timers.values()].find(t => t.index === r.activeTimer);
            const watch = [...this.timers.values()].find(t => t.index === r.stopwatchTimer);
            if (d.flags & 8 && !(d.flags & 16)) return !!active && !(active.flags & 1) && remaining(active, time) > remaining(d, time);
            return d.index === r.activeTimer && (!watch || !!(watch.flags & (1 | 16)) || remaining(watch, time) > remaining(d, time));
        }
        return d.index === r.activeTimer || !!(r.flags & Rule.Koth);
    }
    timerName(d, n) {
        return d.state === 0 ? (this.rules?.halloween === 5 && n <= 5 ? `Merasmus.RoundBegins${n}Seconds` : begins(n)) : `Announcer.RoundEnds${n}seconds`;
    }
    setRow(change, seed) {
        const d = { ...change.components[Component.AnnouncerState], index: change.index, key: change.key, sample: change.sampleTime };
        this.rows.set(change.key, change);
        switch (d.tag) {
            case 1:
                this.rules = d;
                if (seed) this.spies = d.spies;
                if (!this.arena || this.arena.deadline !== d.arenaUnlock) this.arena = { deadline: d.arenaUnlock, armed: new Set([5, 4, 3, 2, 1]), zero: false };
                break;
            case 2: {
                const previous = this.timers.get(d.key);
                d.armed = previous?.armed ?? new Set();
                if (!previous || previous.length !== d.length) {
                    const left = remaining(d, d.sample); d.armed = new Set(thresholds.filter(n => left + (left > 10 ? 2 : 0) >= n));
                }
                this.timers.set(d.key, d);
                if (!seed && previous?.state === 0 && d.state === 1 && this.timerEligible(d, d.sample)) this.cue('Ambient.Siren', `bell:${d.key}:${d.sample}`, d.sample, 5);
                break;
            }
            case 3: this.wave = d; break;
            case 4: {
                const previous = this.points.get(d.key), length = d.capping === 2 ? d.redTime : d.blueTime;
                d.left = previous?.left ?? (d.capping > 1 ? d.lazy * length : 0);
                d.warned = previous?.warned ?? !!d.capping; d.lastWarning = previous?.lastWarning ?? (this.rules ? this.rules.sample - this.rules.gameTime * 1000 : d.sample);
                d.think = previous?.think ?? d.sample;
                d.unlockState = previous?.unlockState;
                if (!previous || previous.unlock !== d.unlock || (previous.flags & 3) !== (d.flags & 3))
                    d.unlockState = { deadline: d.unlock, armed: new Set([5, 4, 3, 2, 1].filter(n => (d.unlock - d.sample) / 1000 >= n)), zero: false };
                if (previous && previous.owner !== d.owner && d.owner === previous.capping) d.left = 0;
                if (previous && previous.capping !== d.capping) {
                    const oldLength = previous.capping === 2 ? previous.redTime : previous.capping === 3 ? previous.blueTime : 0;
                    const percentage = oldLength > 0 ? 1 - previous.left / oldLength : 0;
                    if (!seed && d.warning === 0 && percentage === 0 && d.capping > 1)
                        this.cue(d.owner ? 'Announcer.ControlPointContested' : 'Announcer.ControlPointContested_Neutral', `contested:${d.key}:${d.sample}`, d.sample, 5);
                    d.left = d.capping > 1 && d.capping !== d.owner ? length : 0; d.warned = false; d.think = d.sample;
                }
                if (previous && previous.lazy !== d.lazy) d.left = d.lazy * length;
                this.points.set(d.key, d); break;
            }
            case 5:
                if (!this.seen.has(d.serial)) { this.seen.add(d.serial); if (!seed) this.event(d); }
                if (this.seen.size > 1024) this.seen.delete(this.seen.values().next().value);
                break;
        }
    }
    event(d) {
        const r = this.rules; if (!r) return;
        const id = `event:${d.serial}`;
        if (d.kind === 1 && r.flags & Rule.Mvm) {
            if (d.b > 0 && this.spies === 0) this.cue('Announcer.MVM_Spy_Alert', id, d.start, 5);
            if (d.b === 0 && this.spies > 0) this.cue('Announcer.mvm_spybot_death_all', id, d.start, 5);
            this.spies = d.b;
        } else if (d.kind === 2) this.spies = 0;
        else if (d.kind === 3 && d.state === 4 && !(d.c & 32) && d.b === 0) {
            if (r.flags & Rule.Mvm && d.a === 2) this.cue('Announcer.MVM_All_Dead', id, d.start, 5);
            else if (r.flags & Rule.Competitive && d.a > 1) this.cue(d.a === 2 ? 'Announcer.TeamWipeRed' : 'Announcer.TeamWipeBlu', id, d.start, 5);
        } else if (d.kind === 4 && r.flags & Rule.Arena && d.a > 1 && d.b !== 1 && d.b === d.c)
            this.flawless = { time: d.start + d.delay * 1000, id };
        else if (d.kind === 5) this.flawless = null;
    }
    remove(key) {
        const row = this.rows.get(key); const tag = row?.components[Component.AnnouncerState]?.tag;
        if (tag === 1) this.rules = null; if (tag === 3) this.wave = null;
        this.rows.delete(key); this.timers.delete(key); this.points.delete(key);
    }
    unlock(s, time, arena, id) {
        if (!s || s.deadline < 0) return;
        const left = (arena ? Math.ceil : Math.trunc)((s.deadline - time) / 1000);
        if (left > 5 || left <= 0) {
            if (left <= 0 && s.zero) { s.zero = false; this.cue('Announcer.AM_CapEnabledRandom', `${id}:enabled:${s.deadline}`, time, 5); s.deadline = -1; }
            if (arena) s.armed = new Set([5, 4, 3, 2, 1]);
            return;
        }
        const n = [5, 4, 3, 2, 1].find(n => left <= n && s.armed.has(n));
        if (n) { s.armed.delete(n); if (n === 1) s.zero = true; this.cue(begins(n), `${id}:${s.deadline}:${n}`, time); }
    }
    waveName(tournament) {
        const w = this.wave, r = this.rules; if (!w) return null;
        if (w.wave + (tournament ? 0 : 1) >= w.maxWave) return 'Announcer.MVM_Final_Wave_Start';
        if (w.wave <= (tournament ? 1 : 0)) return tournament && r.mannUp ? 'Announcer.MVM_Manned_Up' : 'Announcer.MVM_First_Wave_Start';
        return 'Announcer.MVM_Wave_Start';
    }
    restartName(n) {
        const r = this.rules;
        if (n === 10 && r.flags & Rule.Mvm) return this.waveName(true);
        if (n === 9 && r.flags & Rule.Mvm && this.wave) {
            const w = this.wave; return w.wave === w.maxWave ? 'music.mvm_start_last_wave' : w.tanks ? 'music.mvm_start_tank_wave' : w.wave > w.maxWave / 2 ? 'music.mvm_start_mid_wave' : 'music.mvm_start_wave';
        }
        if (r.flags & Rule.Competitive) return n === 60 || n === 30 || n === 10 ? `Announcer.CompGame1Begins${n}Seconds` : n >= 1 && n <= 5 ? `Announcer.CompGameBegins0${n}Seconds` : null;
        return n === 10 || n >= 1 && n <= 5 ? begins(n) : null;
    }
    frame(time, audible = true) {
        this.audible = audible;
        const discontinuity = this.time > 0 && time - this.time > 1500;
        this.time = time;
        while (this.pending.length && this.pending[0].time <= time) {
            // Seed when playback reaches the baseline. Deltas received during
            // the initial interpolation delay still contain new public events.
            const p = this.pending.shift(); const seed = this.first || p.replace || discontinuity;
            if (p.replace) {
                this.rows.clear(); this.timers.clear(); this.points.clear(); this.rules = this.wave = this.arena = null;
                this.flawless = null; this.restartTime = this.waveTime = null;
            }
            if (p.replace) for (const key of this.rows.keys()) if (!p.present.has(key)) this.remove(key);
            for (const key of p.deleted) this.remove(key);
            // Rules/objective dependencies precede transitions regardless of row order.
            for (const c of [...p.changes].sort((a, b) => a.components[Component.AnnouncerState].tag - b.components[Component.AnnouncerState].tag)) this.setRow(c, seed);
            this.first = false;
        }
        const r = this.rules; if (!r) return;
        for (const d of this.timers.values()) {
            const left = remaining(d, time);
            if (discontinuity) d.armed = new Set(thresholds.filter(n => left >= n + 1));
            for (const n of d.armed) this.prepare(this.timerName(d, n));
            if (d.flags & (1 | 2) || (d.flags & 8 && d.flags & 16)) continue;
            const n = thresholds.find(n => left <= n + 1 && d.armed.has(n));
            if (n) { d.armed.delete(n); if (this.timerEligible(d, time)) this.cue(this.timerName(d, n), `timer:${d.key}:${d.length}:${n}`, time); }
        }
        if (r.flags & Rule.Arena && r.state === 7) {
            for (let n = 1; n <= 5; n++) this.prepare(begins(n)); this.prepare('Announcer.AM_CapEnabledRandom');
            if (discontinuity && this.arena) this.arena.armed = new Set([5, 4, 3, 2, 1].filter(n => (this.arena.deadline - time) / 1000 >= n));
            this.unlock(this.arena, time, true, 'arena');
        }
        for (const d of this.points.values()) {
            if ((d.flags & 3) === 3 && !(r.flags & Rule.Waiting) && r.state === 4) {
                for (let n = 1; n <= 5; n++) this.prepare(begins(n)); this.prepare('Announcer.AM_CapEnabledRandom');
                if (discontinuity) { d.unlockState.zero = false; d.unlockState.armed = new Set([5, 4, 3, 2, 1].filter(n => (d.unlockState.deadline - time) / 1000 >= n)); }
                this.unlock(d.unlockState, time, false, `point:${d.key}`);
            }
            if (d.warnSound) this.prepare(d.warnSound);
            if (time - d.think < 100) continue;
            const dt = Math.min((time - d.think) / 1000, 1); d.think = time;
            if (!d.left || d.flags & 4) continue;
            const count = d.zone === 2 ? d.redPlayers : d.zone === 3 ? d.bluePlayers : 0;
            let reduction = dt;
            if (r.capStyle === 1 && d.flags & 8) for (let p = 1; p < count; p++) reduction += dt / (p + 1);
            if (count > 0 && d.zone === d.capping) {
                d.left -= reduction;
                if (!d.warned && d.capping > 1 && d.warning === 1 && time > d.lastWarning + 5000) {
                    d.warned = true; d.lastWarning = time; this.cue(d.warnSound, `final:${d.key}:${time}`, time, 5);
                }
            } else if (count > 0 && d.owner === 0 && d.zone !== 0) d.left += reduction;
            else {
                const length = d.capping === 2 ? d.redTime : d.blueTime;
                const canCap = d.capping === 2 ? d.flags & 16 : d.flags & 32;
                d.left = canCap ? d.left + length / Math.max(0.001, d.flags & 8 ? r.deteriorate : length) * dt * (r.flags & Rule.Overtime ? 6 : 1) : 0;
                d.warned = false;
            }
        }
        const deadline = r.restart > time ? r.restart : r.countdown > time ? r.countdown : -1;
        const n = deadline < 0 ? 0 : Math.ceil((deadline - time) / 1000);
        for (const next of [60, 30, 10, 9, 5, 4, 3, 2, 1]) if (next <= n) { const name = this.restartName(next); if (name) this.prepare(name); }
        if (this.restartTime !== n) { if (this.restartTime !== null && !discontinuity) this.cue(this.restartName(n), `restart:${deadline}:${n}`, time, n === 9 ? 5 : 1); this.restartTime = n; }
        if (r.flags & Rule.Mvm && this.wave && r.state > 1) {
            const timer = [...this.timers.values()].find(t => t.index === r.activeTimer);
            let n = this.wave.nextWave >= 0 ? Math.max(0, Math.trunc((this.wave.nextWave - time) / 1000)) : 0;
            if (r.flags & Rule.Setup && !(r.flags & Rule.Tournament) && timer) n = Math.trunc(remaining(timer, time));
            if (n >= 10) this.prepare(this.waveName(false));
            for (let next = 1; next <= Math.min(n, 5); next++) this.prepare(begins(next));
            if (n !== this.waveTime) {
                if (this.waveTime !== null && !discontinuity) this.cue(n === 10 ? this.waveName(false) : n >= 1 && n <= 5 ? begins(n) : null, `wave:${this.wave.nextWave}:${n}`, time);
                this.waveTime = n;
            }
        }
        if (this.flawless) {
            this.prepare('Announcer.AM_FlawlessVictory01');
            if (time > this.flawless.time) { this.cue('Announcer.AM_FlawlessVictory01', this.flawless.id, this.flawless.time, 5); this.flawless = null; }
        }
    }
}

/** Sound scripts share LiveSounds' channel ownership and audio preferences. */
export class AnnouncerAudio {
    constructor(sounds, rpc, { random = Math.random, diagnostic = () => {} } = {}) { Object.assign(this, { sounds, rpc, random, diagnostic }); this.clear(); }
    clear() { this.generation = (this.generation ?? 0) + 1; this.scripts = new Map(); this.failures = new Map(); this.closed = false; }
    prepare(entry) {
        if (!entry || this.closed) return Promise.resolve(null);
        const failedAt = this.failures.get(entry);
        if (failedAt !== undefined && this.sounds.time - failedAt < 30000) return Promise.resolve(null);
        if (failedAt !== undefined) { this.failures.delete(entry); this.scripts.delete(entry); }
        if (this.scripts.has(entry)) return this.scripts.get(entry);
        const generation = this.generation;
        const promise = Promise.resolve().then(() => {
            // GetWarnSound may name a raw GAME-relative clip instead of a script.
            if (/\.(wav|mp3)$/i.test(entry)) {
                const path = `sound/${entry.replace(/^[*#><^@)}]+/, '').replaceAll('\\', '/').replace(/[A-Z]/g, c => c.toLowerCase())}`;
                validateDictionary(4, 1, path);
                return { waves: [{ path, modifiers: '' }], channel: 0, volume: { minimum: 1, maximum: 1 }, pitch: { minimum: 100, maximum: 100 }, soundLevel: 75 };
            }
            return this.rpc('sound-script', { entry });
        }).then(script => {
            if (generation !== this.generation || this.closed) return null;
            for (const wave of script.waves.slice(0, 4)) void this.sounds.resource(wave.path).catch(() => {});
            return script;
        }).catch(error => {
            if (generation === this.generation && !this.closed) {
                this.failures.set(entry, this.sounds.time); if (this.failures.size > 128) this.failures.delete(this.failures.keys().next().value);
                this.diagnostic(`Announcer ${entry}: ${error.message}`);
            }
            return null;
        });
        if (this.scripts.size >= 128) this.scripts.delete(this.scripts.keys().next().value);
        this.scripts.set(entry, promise); return promise;
    }
    async emit(cue) {
        const generation = this.generation, script = await this.prepare(cue.entry);
        if (!script || generation !== this.generation || this.closed || this.sounds.time > cue.validUntil) return;
        const wave = script.waves[Math.min(script.waves.length - 1, Math.floor(this.random() * script.waves.length))];
        if (!wave) return;
        const sample = range => range.minimum + this.random() * (range.maximum - range.minimum);
        this.sounds.local(cue.identity, wave.path, { startTime: cue.time, channel: script.channel, volume: sample(script.volume),
            pitch: Math.round(sample(script.pitch)), level: script.soundLevel, flags: 4096, dsp: 0, ownerIndex: 0, ownerGeneration: 0 }, cue.validUntil);
    }
    dispose() { this.clear(); this.closed = true; }
}
